import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Channel, DefaultGenerics } from 'stream-chat'
import { ChatService } from '@/services/Chat/ChatService'
import { ChannelOptions } from '../types'
import { PAGINATION } from '../constants'
import { queryChannelsWithRetry } from '../services/ChatApiService'
import { ChatClientContextV2 } from './ChatClientProviderV2'

export interface ChatChannelListContextV2Value {
  directChannels: Channel<DefaultGenerics>[]
  caseChannels: Channel<DefaultGenerics>[]
  isLoading: boolean
  hasMore: boolean
  loadMore: () => Promise<void>
  selectedTab: ChannelOptions
  setSelectedTab: (tab: ChannelOptions) => void
}

const defaultValue: ChatChannelListContextV2Value = {
  directChannels: [],
  caseChannels: [],
  isLoading: false,
  hasMore: true,
  loadMore: async () => {},
  selectedTab: ChannelOptions.DirectMessages,
  setSelectedTab: () => {},
}

export const ChatChannelListContextV2 =
  createContext<ChatChannelListContextV2Value>(defaultValue)

export const ChatChannelListProviderV2: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { client, currentUserId, connectionState } =
    useContext(ChatClientContextV2)
  const [directChannels, setDirectChannels] = useState<
    Channel<DefaultGenerics>[]
  >([])
  const [caseChannels, setCaseChannels] = useState<Channel<DefaultGenerics>[]>(
    []
  )
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [selectedTab, setSelectedTab] = useState<ChannelOptions>(
    ChannelOptions.DirectMessages
  )

  const getChannelsForTab = useCallback(
    (tab: ChannelOptions) =>
      tab === ChannelOptions.DirectMessages ? directChannels : caseChannels,
    [directChannels, caseChannels]
  )

  const setChannelsForTab = useCallback(
    (
      tab: ChannelOptions,
      updater: (prev: Channel<DefaultGenerics>[]) => Channel<DefaultGenerics>[]
    ) => {
      if (tab === ChannelOptions.DirectMessages) {
        setDirectChannels(updater)
      } else {
        setCaseChannels(updater)
      }
    },
    []
  )

  const fetchChannels = useCallback(
    async (tab: ChannelOptions, offset: number) => {
      if (!client || !currentUserId) return

      setIsLoading(true)
      try {
        const channels = await queryChannelsWithRetry(
          client,
          {
            type: 'messaging',
            channel_type: tab,
            members: { $in: [currentUserId] },
          },
          { last_message_at: -1 },
          { limit: PAGINATION.defaultPageSize, offset, state: true }
        )

        if (offset === 0) {
          setChannelsForTab(tab, () => channels)
        } else {
          setChannelsForTab(tab, prev => {
            const existingIds = new Set(prev.map(c => c.cid))
            const newChannels = channels.filter(c => !existingIds.has(c.cid))
            return [...prev, ...newChannels]
          })
        }

        setHasMore(channels.length === PAGINATION.defaultPageSize)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('ChatV2: Failed to fetch channels:', error)
        setHasMore(false)
      } finally {
        setIsLoading(false)
      }
    },
    [client, currentUserId, setChannelsForTab]
  )

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return
    const currentChannels = getChannelsForTab(selectedTab)
    await fetchChannels(selectedTab, currentChannels.length)
  }, [isLoading, hasMore, selectedTab, getChannelsForTab, fetchChannels])

  // Fetch both channel types on initial connection so "All" filter works
  const bothTabsFetched = useRef(false)

  // Initial load and tab change
  useEffect(() => {
    if (connectionState.status !== 'connected') return
    setHasMore(true)
    fetchChannels(selectedTab, 0)

    if (!bothTabsFetched.current) {
      bothTabsFetched.current = true
      const otherTab =
        selectedTab === ChannelOptions.DirectMessages
          ? ChannelOptions.CaseMessages
          : ChannelOptions.DirectMessages
      fetchChannels(otherTab, 0)
    }
  }, [connectionState.status, selectedTab, fetchChannels])

  // Bug fix #8: re-sort channel list on new messages
  useEffect(() => {
    if (!client) return undefined

    const { unsubscribe } = client.on(event => {
      if (
        event.type !== 'message.new' &&
        event.type !== 'notification.message_new'
      )
        return

      // Hidden system messages produce no visible activity, so they must
      // not bump the channel to the top or pull new channels into the list
      if (ChatService.isSystemMessage(event.message)) return

      const channelType = event.channel?.channel_type as
        | ChannelOptions
        | undefined
      if (!channelType) return

      const channelCid = event.cid

      setChannelsForTab(channelType, prev => {
        const existingIndex = prev.findIndex(c => c.cid === channelCid)

        if (existingIndex > 0) {
          // Splice to top
          const channel = prev[existingIndex]
          const updated = [...prev]
          updated.splice(existingIndex, 1)
          return [channel, ...updated]
        }

        if (existingIndex === -1 && client && currentUserId) {
          // New channel not in list - fetch and prepend
          const channelId = event.channel_id
          if (channelId) {
            client
              .queryChannels(
                { id: channelId, members: { $in: [currentUserId] } },
                {},
                { limit: 1 }
              )
              .then(channels => {
                if (channels[0]) {
                  setChannelsForTab(channelType, current => {
                    if (current.some(c => c.cid === channels[0].cid)) {
                      return current
                    }
                    return [channels[0], ...current]
                  })
                }
              })
              .catch(() => {
                // Non-critical: channel will appear on next load
              })
          }
        }

        return prev
      })
    })

    return () => {
      unsubscribe()
    }
  }, [client, currentUserId, setChannelsForTab])

  const value = useMemo(
    () => ({
      directChannels,
      caseChannels,
      isLoading,
      hasMore,
      loadMore,
      selectedTab,
      setSelectedTab,
    }),
    [directChannels, caseChannels, isLoading, hasMore, loadMore, selectedTab]
  )

  return (
    <ChatChannelListContextV2.Provider value={value}>
      {children}
    </ChatChannelListContextV2.Provider>
  )
}
